import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { CalendarYearPickerConfig } from './calendar-year-picker-config';

const YEARS_PER_PAGE = 24;

function euclideanModulo(a: number, b: number): number {
  return ((a % b) + b) % b;
}

/**
 * Custom calendar header that replaces the default Angular Material header.
 * Clicking the period label toggles between month-view and multi-year-view,
 * letting users jump directly to any year rather than paging month-by-month.
 */
@Component({
  selector: 'spri-custom-calendar-header',
  standalone: true,
  imports: [MatIconModule, MatButtonModule],
  templateUrl: './custom-calendar-header.html',
  styleUrls: ['./custom-calendar-header.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomCalendarHeader<D = unknown> {
  private readonly calendar = inject(MatCalendar) as MatCalendar<D>;
  private readonly adapter = inject(DateAdapter) as DateAdapter<D>;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly yearPickerConfig = inject(CalendarYearPickerConfig, { optional: true });

  public label = this.buildLabel();
  public isMonthView = this.calendar.currentView === 'month';
  public readonly isYearPickerEnabled = this.yearPickerConfig?.enabled ?? false;

  constructor() {
    this.calendar.stateChanges.pipe(takeUntilDestroyed()).subscribe((): void => {
      this.label = this.buildLabel();
      this.isMonthView = this.calendar.currentView === 'month';
      this.cdr.markForCheck();
    });
  }

  /** Toggles between the month grid and the multi-year grid (no-op when disabled). */
  public handlePeriodLabel(): void {
    if (!this.isYearPickerEnabled) return;
    this.calendar.currentView = this.isMonthView ? 'multi-year' : 'month';
  }

  public prev(): void {
    this.calendar.activeDate = this.navigated(-1);
  }

  public next(): void {
    this.calendar.activeDate = this.navigated(1);
  }

  private navigated(direction: number): D {
    const view = this.calendar.currentView;
    if (view === 'month') {
      return this.adapter.addCalendarMonths(this.calendar.activeDate, direction);
    }
    if (view === 'year') {
      return this.adapter.addCalendarYears(this.calendar.activeDate, direction);
    }
    return this.adapter.addCalendarYears(this.calendar.activeDate, direction * YEARS_PER_PAGE);
  }

  private buildLabel(): string {
    const view = this.calendar.currentView;
    if (view === 'month') {
      return this.adapter.format(this.calendar.activeDate, {
        month: 'long',
        year: 'numeric',
      } as unknown as Intl.DateTimeFormatOptions);
    }
    if (view === 'year') {
      return this.adapter.getYearName(this.calendar.activeDate);
    }
    return this.buildMultiYearLabel();
  }

  private buildMultiYearLabel(): string {
    const activeYear = this.adapter.getYear(this.calendar.activeDate);
    const offset = euclideanModulo(activeYear, YEARS_PER_PAGE);
    const startYear = activeYear - offset;
    const endYear = startYear + YEARS_PER_PAGE - 1;
    const startDate = this.adapter.createDate(startYear, 0, 1);
    const endDate = this.adapter.createDate(endYear, 0, 1);
    return `${this.adapter.getYearName(startDate)} \u2013 ${this.adapter.getYearName(endDate)}`;
  }
}
